import React, {
    useCallback,
    useRef,
    useImperativeHandle,
    useMemo,
} from "react";
import type { MouseEventHandler, Ref } from "react";

import cn from "classnames";

import { useActivatableElement } from "../../../hooks/useActiveElement";
import { useOverlays } from "../../../hooks/useOverlays";
import { Overlay } from "../../overlays/Overlay/Overlay";
import { Box, boxForwardRef } from "../Box/Box";
import { Portal } from "../Portal/Portal";

import type { PropsWithTsId } from "../../../types/react";
import type { BoxComponentWithDefault } from "../Box/Box";

import classes from "./ButtonCore.styl";

type IButtonCore = PropsWithTsId<{
    /** Селектор обёртки детей (это либо button, либо ButtonDiv) */
    wrapperClassName?: string;
    /** Селектор контейнера кнопки */
    containerClassName?: string;
    /** Селектор обёртки оверлеев */
    overlaysClassName?: string;
    /** Контейнер для оверлеев, если их нужно размещать не под root элементом */
    overlaysContainer?: React.RefObject<HTMLElement>;
    /** Если true, то обработчики анимации добавлены не будут */
    disableAnimation?: boolean;
}>;

/**
 * Компонент кнопки, реализующий всю логику анимации с оверлеями при клике на компонент
 */
export const ButtonCore = boxForwardRef<
    BoxComponentWithDefault<"button", IButtonCore>
>(
    (
        {
            as = "button",
            disableAnimation = false,
            className,
            wrapperClassName,
            overlaysClassName,
            overlaysContainer,
            containerClassName,
            onMouseEnter,
            onMouseDown,
            children,
            "data-tsid": tsid,
            ...buttonProps
        },
        ref: Ref<HTMLElement | null>,
    ) => {
        const buttonRef = useRef<HTMLElement>(null);
        const buttonRectLeft = useRef<number>(0);
        const buttonRectTop = useRef<number>(0);
        const overlayElRef = overlaysContainer || buttonRef;

        useImperativeHandle(ref, () => buttonRef.current);

        const { overlays, addOverlay, createRemoveOverlayHandler } =
            useOverlays({ additionalSpase: 150 });
        const { isElementActive, onActivatableElementMouseDown } =
            useActivatableElement();

        const mouseEnterHandler: MouseEventHandler<HTMLElement> = useCallback(
            (e) => {
                onMouseEnter?.(e);
                const { left, top } =
                    buttonRef.current!.getBoundingClientRect();

                buttonRectLeft.current = left;
                buttonRectTop.current = top;
            },
            [onMouseEnter],
        );

        const mouseDownHandler: MouseEventHandler<HTMLElement> = useCallback(
            (e) => {
                onMouseDown?.(e);

                if (!overlayElRef.current) {
                    return;
                }
                addOverlay(overlayElRef.current, e);
                onActivatableElementMouseDown(e);
            },
            [
                addOverlay,
                onActivatableElementMouseDown,
                onMouseDown,
                overlayElRef,
            ],
        );

        const currentProps = useMemo(() => {
            if (disableAnimation) {
                return {
                    onMouseEnter,
                    onMouseDown,
                };
            }
            return {
                onMouseEnter: mouseEnterHandler,
                onMouseDown: mouseDownHandler,
            };
        }, [
            disableAnimation,
            mouseDownHandler,
            mouseEnterHandler,
            onMouseDown,
            onMouseEnter,
        ]);

        const overlayElements = Boolean(overlays.length) && (
            <div
                className={cn(classes["overlays-container"], overlaysClassName)}
            >
                {overlays.map(({ key, ...props }) => (
                    <Overlay
                        key={key}
                        {...props}
                        removeOverlay={createRemoveOverlayHandler(key)}
                        finishAnimation={!isElementActive}
                    />
                ))}
            </div>
        );

        return (
            <Box
                as={as}
                {...buttonProps}
                {...currentProps}
                data-tsid={tsid}
                ref={buttonRef}
                className={cn(classes["container"], containerClassName)}
            >
                <div
                    className={cn(
                        classes["wrapper"],
                        wrapperClassName,
                        className,
                    )}
                >
                    {children}

                    {overlaysContainer?.current ? (
                        <Portal
                            getContainer={() => overlaysContainer.current!}
                            data-tsid="overlay"
                        >
                            {overlayElements}
                        </Portal>
                    ) : (
                        overlayElements
                    )}
                </div>
            </Box>
        );
    },
);

ButtonCore.displayName = "ButtonCore";
